<script setup lang="ts">
const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

definePageMeta({
  layout: "static",
});

useSeoMeta({
  title: "Customer Terms of Use | &Dine",
  description:
    "Customer terms and conditions for ordering through &Dine. Read the terms that apply when you order office catering from our Makers.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/customer-terms` }],
});
</script>

<template>
  <section class="customer-terms">
    <h1>&amp;Dine Customer Terms of Use</h1>
    <p>
      Welcome to &amp;Dine. This page (together with the documents referred to in it) tells you the
      terms (the &ldquo;
      <strong>Terms</strong>
      &rdquo;) which apply when you order any menu items (the
      <strong>&quot;Items&quot;</strong>
      ) from our website or mobile applications and related services (each referred to as an
      <strong>&ldquo;Application&rdquo;</strong>
      ).
    </p>
    <p>
      Please read these Terms carefully before creating an &amp;Dine account or using our
      Application. If you have any questions relating to these Terms please contact
      <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
      . If you are a consumer, you have certain legal rights when you order Items using our
      Application. You can find more information about these rights at:
      <a
        href="https://www.citizensadvice.org.uk/consumer/"
        target="_blank"
      >
        https://www.citizensadvice.org.uk/consumer/
      </a>
      . Your legal rights are not affected by these Terms, which apply in addition to them and do
      not replace them. &nbsp;By setting up your &amp;Dine account, you confirm that you accept
      these Terms.
    </p>
    <p><br /></p>
    <p><strong>1. Information About Us</strong></p>
    <p>
      anddine.co.uk is operated by &amp;DINE Ltd, a company incorporated and registered in England
      and Wales, whose registered office is at Beyond, Fox Court, 14 Gray&rsquo;s Inn Road, London,
      WC1X 8HN. Our Company registration number is 12713508. You may contact us at
      <a href="mailto:hello@anddine.co.uk">hello@anddine.co.uk</a>
    </p>
    <p><br /></p>
    <p><strong>2. Purpose</strong></p>
    <p>
      Our objective is to link you to the businesses we partner with (&ldquo;
      <strong>Partners</strong>
      &quot;) and allow you to order Items for delivery (our &ldquo;
      <strong>Service</strong>
      &rdquo;). Where you order from a Partner, &amp;Dine acts as an agent on behalf of that Partner
      to conclude your order from our Application and to manage your experience throughout the order
      process. Once you have placed an order, delivery of your Items will be arranged by &amp;Dine
      (the &ldquo;
      <strong>Delivery</strong>
      &rdquo;).&nbsp;
    </p>
    <p><br /></p>
    <p><strong>3. Your Account</strong></p>
    <p>
      Before you can place orders for Items using our Application, you need to open an &amp;Dine
      account. When you open an account you may create a password, or other secure login method, and
      may also have to provide credit card details. You must keep any password you create, or other
      secure login method, secret, and prevent others from accessing your email account or mobile
      phone. If another person uses these methods to access your account, you will be responsible to
      pay for any Items they order, and we are not responsible for any other losses you suffer,
      unless the person using your password obtained it because we did not keep it secure.
    </p>
    <p>
      You may close your account at any time by requesting to do so in your account section of our
      website or contacting us using the contact details above. We may suspend your access to your
      account, or close it permanently, if we believe that your account has been used by someone
      else. We may also close your account if in our opinion you are abusing our Service (for
      example, by applying for refunds or credit to which we do not consider you are entitled,
      making repeated unreasonable complaints, mistreating our staff or riders, or any other good
      reason). If we close your account permanently we will refund any remaining account credit you
      have validly obtained from our customer service team or Application following any issue with
      an order, by applying a credit to your registered credit card, or if that is not possible for
      any reason, by way of a bank transfer using bank details (provided you have supplied them to
      us).
    </p>
    <p><br /></p>
    <p><strong>4. Service Availability</strong></p>
    <p>
      Our Partners&rsquo; availability, both with respect to delivery area and lead time for orders,
      is set out in the Application.&nbsp;&nbsp;All Items are delivered cold.&nbsp;&nbsp;The
      availability of our Service, and the range of Partners from which you can order, depends on
      the Partners in your area and their capacity. If they are unable to satisfy your order you
      will be notified.&nbsp;
    </p>
    <p><br /></p>
    <p><strong>5. Orders</strong></p>
    <p>
      You must be aged 18 or older to use our website and Services. By placing an order through our
      Application, you confirm that you are aged 18 or older.&nbsp;
    </p>
    <p>
      When you place an order through our Application, it needs to be accepted by us or the Partner
      before it is confirmed. We will send you a notification if your order has been accepted
      (the&nbsp;&quot;
      <strong>Confirmation Notice</strong>
      &quot;). The contract for the supply of any Item you have ordered comes into existence when we
      send the Confirmation Notice. You are responsible for paying for all Items ordered using your
      account, and for related delivery charges, and for complying with these Terms, even if you
      have ordered the Item for someone else.&nbsp;
    </p>
    <p>
      Where a minimum order value policy applies, this will be displayed on our Application. All
      Items are subject to availability. &nbsp;Partners may use nuts or other allergens, dairy
      products or regional spices in the preparation of certain Items. Increasing numbers of
      Partners will be displaying dish by dish allergens information, together with other
      ingredients. Where that information is not available or if you have further questions, please
      contact the Partner prior to ordering if you have an allergy. &amp;Dine cannot guarantee that
      any of the Items sold by our Partners are free of allergens; and if you have a severe allergy
      &amp;Dine would advise you not to order via the Application.
    </p>
    <p><br /></p>
    <p><strong>6. Delivery</strong></p>
    <p>
      When you place an order you will be notified of the available delivery time
      period.&nbsp;&nbsp;The Confirmation Notice will state the day on which the Item is expected to
      be delivered, between the hours of 10am and 6pm. Once the delivery has been despatched, you
      may track your delivery via the Application to find out an approximate delivery time; however,
      you must be available to accept delivery for thirty minutes before and thirty minutes after
      that time.&nbsp;
    </p>
    <p>
      It is important that you always check our Application for accurate delivery times, as this can
      vary for a number of reasons including traffic and weather conditions and delivery
      availability.&nbsp;Please ensure that someone is available to accept delivery, or
      alternatively if you need the delivery to be left in a safe place please note that your order
      contains chilled food, so any safe place must also be cool. If your order contains dairy
      products, or other heat sensitive products, it is your responsibility to ensure that your
      delivery remains cool at all times prior to use.&nbsp;
    </p>
    <p>
      Unfortunately, despite our, and our Partner&apos;s best efforts, things do not always go to
      plan and factors may prevent us from delivering your Item on time. If your order is more than
      one hour later than the end time for delivery that we communicated to you in the Confirmation
      Notice, and we have not notified you giving you the option to cancel your order, we will work
      with you to make things right unless you have caused the delay (e.g. because you gave us the
      wrong address or did not come to the door).
    </p>
    <p>
      We will attempt delivery at the address you provide to us when you place your order. If you
      need to change the delivery location after you have placed your order, we may be able to
      change to the address to an alternative one that is registered with your account if you let us
      know within 4 hours of the order being placed. If we cannot change the delivery address, you
      have the option to cancel the order, but if food preparation has started you will be charged
      the full price for the Item, and if the delivery has been despatched you will also be charged
      for delivery.
    </p>
    <p>
      You will still be charged for the Item and for delivery in the event of a failed delivery if
      you have caused such failure for any reason. Reasons you might cause a delivery to fail
      include (but are not limited to): &nbsp;&nbsp;
    </p>
    <ul>
      <li>
        You do not come to the door, did not pick up the phone when the delivery contacted you using
        the contact information you have provided us and/or you picked up the phone but then failed
        to provide access within a reasonable amount of time, and the delivery is unable to find a
        safe location to leave the food.
      </li>
    </ul>
    <p><br /></p>
    <p><strong>7. Your Rights if Something is Wrong With Your Items</strong></p>
    <p>
      You have a legal right to receive goods which comply with their description, which are of
      satisfactory quality and which comply with any specific requirements you tell us about (and we
      agree to) before you place your order. If you believe that the Items you have been delivered
      do not comply with these legal rights, please let us know. We may request a photograph or
      other evidence showing the problem if it is something that can be seen by inspecting the
      Items. We will provide a refund or account credit in respect of the affected part of the Item,
      and also in respect of delivery if the whole order was affected, unless we have reasonable
      cause to believe that the problem was caused after delivery.
    </p>
    <p>
      Prior to processing your refund or account credit, we may take into account relevant factors
      including the details of the order, including your account history, what happened on delivery
      and information from the Partner.
    </p>
    <p><br /></p>
    <p><strong>8. Cancellation</strong></p>
    <p>
      You may cancel an order without charge at any time before the Partner has started preparing
      the food (a&nbsp;&quot;
      <strong>Started Order</strong>
      &quot;). If you wish to cancel an order before it becomes a Started Order, please contact us
      immediately, via our Application. If the Partner confirms the order was not a Started Order,
      we will refund your payment. If you cancel any order after it becomes a Started Order, you
      will be charged the full price for the Items, and if the delivery has been despatched you will
      also be charged for delivery. &nbsp;
    </p>
    <p>
      &amp;Dine and the Partner may notify you that an order has been cancelled at any time. You
      will not be charged for any orders cancelled by us or the Partner, and we will reimburse you
      for any payment already made using the same method you used to pay for your order. We may also
      apply credit to your account to reflect the inconvenience caused.
    </p>
    <p><br /></p>
    <p><strong>9. Prices and Payment&nbsp;</strong></p>
    <p>
      Prices are not currently subject to VAT, but in the event of a change in applicable
      legislation then prices would be adjusted to include VAT. You confirm that you are using our
      Service for personal, non-commercial use. Prices may change at any time at the discretion of
      &amp;Dine or the Partner. We reserve the right to charge a Service Fee (which may become
      subject to VAT),&nbsp;which may be subject to change,&nbsp;for the provision of our
      Services.&nbsp;You will be notified of any applicable Service Fee and taxes prior to purchase
      on the checkout page on our Application.&nbsp;
    </p>
    <p>
      No changes will affect existing confirmed orders, unless there is an obvious pricing mistake.
      Nor will changes to prices affect any orders in process and appearing within your basket,
      provided you complete the order within 2 hours of creating the basket. If you do not conclude
      the order before the 2 hour cut-off the items will be removed from your basket automatically
      and the price change will apply.&nbsp;
    </p>
    <p>
      If there is an obvious pricing mistake we will notify you as soon as we can and you will have
      the choice of confirming the order at the original price or cancelling the order without
      charge and with a full refund of any money already paid. &nbsp;Where &amp;Dine makes a
      delivery, we may also charge you a delivery fee. This will be notified to you during the order
      process before you complete your order. The total price of your order will be set out on the
      checkout page on our Application, including the prices of Items and Delivery and applicable
      Service Fees and taxes.
    </p>
    <p>
      Payment for all Items and deliveries can be made on our Application by credit or debit card,
      or other payment method made available by &amp;Dine. Once your order has been confirmed your
      credit or debit card will be authorised and the total amount marked for payment. Payment is
      made directly to &amp;Dine acting as agent on behalf of the Partner only.&nbsp;
    </p>
    <p>
      We are authorised by our Partners to accept payment on their behalf and payment of the price
      of any Items or delivery charges to us will fulfil your obligation to pay the price to the
      Partner.&nbsp;
    </p>
    <p><br /></p>
    <p><strong>10. Our Responsibility for Loss or Damage That You Suffer</strong></p>
    <p>
      We are responsible to you for any loss or damage that you suffer that is a foreseeable result
      of our breaking these Terms or of failing to use reasonable care and skill in relation to your
      use of our Service. We are not responsible for any loss or damage that is not foreseeable.
      Loss or damage is &ldquo;foreseeable&rdquo; if it is either obvious that it will happen, or if
      you told us that it might happen, for example if you tell us about particular circumstances
      that might increase the loss or damage arising from our breach of these Terms before you place
      an order.
    </p>
    <p>
      We do not exclude or limit our responsibility to you for loss or damage where it would be
      unlawful to do so. This includes any responsibility for death or personal injury caused by our
      failure, or our employees&rsquo;, agents&rsquo; or subcontractors&rsquo; failure, to use
      reasonable care and skill; for fraud or fraudulent misrepresentation; for breach of your legal
      rights in relation to the Items, as summarised at part 7 above; or for defective Items under
      the Consumer Protection Act 1987. Subject to the previous paragraph, we are not responsible
      for any loss or damage that you suffer as a result of your own breach of these Terms, or as a
      result of any IT hardware or software failure other than a failure in our Applications. &nbsp;
    </p>
    <p><br /></p>
    <p><strong>11. Data Protection</strong></p>
    <p>
      We process your personal data in accordance with our Privacy Policy which can be found
      <a
        href="/privacy-policy"
        target="_blank"
      >
        here
      </a>
      .
    </p>
    <p><br /></p>
    <p><strong>12. Other Terms</strong></p>
    <p>
      If either we or you have any right to enforce these Terms against the other, that right will
      not be lost even if the person who has the right delays enforcing it, or waives their right to
      enforce it in any instance. &nbsp;If a court or other authority decides that any part of these
      Terms is illegal or ineffective, the rest of the terms will be unaffected and will remain in
      force.
    </p>
    <p>
      We may change these Terms from time to time. If we make any changes which affect your rights
      in relation to our Service, we will notify you. Changes to the Terms will not affect any
      orders you have placed where we have sent the Confirmation Notice.&nbsp;
    </p>
    <p>
      We may transfer our rights and obligations under these Terms to another organisation, however
      you need our consent to transfer your rights to someone else. If a court finds part of this
      contract illegal, the rest will continue in force. Even if we delay in enforcing this
      contract, we can still enforce it later.&nbsp;
    </p>
    <p>
      These Terms are governed by English law and you can bring legal proceedings in relation to our
      Service in the English courts. If you live in Scotland you can bring legal proceedings in
      respect of the Items in either the Scottish or the English courts. If you live in Northern
      Ireland you can bring legal proceedings in respect of the Items in either the Northern Irish
      or the English courts.
    </p>
  </section>
</template>

<style lang="scss">
.customer-terms {
  width: 90%;
  padding: 50px 5%;
  background-color: var(--color-white-1);
  margin-top: 80px;
  @media (max-width: $tablet) {
    margin-top: 70px;
  }
  p {
    color: var(--color-grey-1);
  }
}
</style>
